import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { isImage, isPdf } from "@sp-crm/core";
import React, { useState } from "react";
import { DocumentRender } from "../files/pdf/document-render";
import { PanelType } from "../ui/panel/panel-type";
import { ResponsivePanel } from "../ui/panel/responsive-panel";
import { PrimaryButton } from "../ui/primary-button";
import { SecondaryButton } from "../ui/secondary-button";

export type AttachmentAddToRecordState =
    | { kind: "idle"; label: string; tooltip: string; onAdd: () => void }
    | { kind: "pending" }
    | { kind: "added" };

interface AttachmentPreviewPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    filename: string;
    contentType: string;
    previewUrl: string;
    downloadUrl: string;
    addToRecord: AttachmentAddToRecordState;
}

export const AttachmentPreviewPanel: React.FC<AttachmentPreviewPanelProps> = props => {
    const {
        isOpen,
        onDismiss,
        filename,
        contentType,
        previewUrl,
        downloadUrl,
        addToRecord,
    } = props;

    return (
        <ResponsivePanel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.extraLarge}
            mobileVariant="fullscreen"
            headerText={filename}>
            <PreviewBody
                filename={filename}
                contentType={contentType}
                previewUrl={previewUrl}
                downloadUrl={downloadUrl}
                addToRecord={addToRecord}
            />
        </ResponsivePanel>
    );
};

interface PreviewBodyProps {
    filename: string;
    contentType: string;
    previewUrl: string;
    downloadUrl: string;
    addToRecord: AttachmentAddToRecordState;
}

const PreviewBody: React.FC<PreviewBodyProps> = props => {
    const { filename, contentType, previewUrl, downloadUrl, addToRecord } = props;

    if (isImage(contentType)) {
        return (
            <div className="flex flex-col gap-3">
                <PreviewActionBar addToRecord={addToRecord} downloadUrl={downloadUrl} />
                <div className="flex justify-center">
                    <img
                        src={previewUrl}
                        alt={filename}
                        className="max-w-full max-h-[80vh]"
                    />
                </div>
            </div>
        );
    }

    if (isPdf(contentType)) {
        return (
            <PdfPreview
                previewUrl={previewUrl}
                downloadUrl={downloadUrl}
                addToRecord={addToRecord}
            />
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <PreviewActionBar addToRecord={addToRecord} downloadUrl={downloadUrl} />
            <p className="text-sm text-gray-600 text-center py-8">
                This file type cannot be previewed in the browser. Download it to view.
            </p>
        </div>
    );
};

interface PdfPreviewProps {
    previewUrl: string;
    downloadUrl: string;
    addToRecord: AttachmentAddToRecordState;
}

const PdfPreview: React.FC<PdfPreviewProps> = props => {
    const { previewUrl, downloadUrl, addToRecord } = props;
    const [pageNumber, setPageNumber] = useState(1);
    return (
        <div className="flex flex-col gap-3">
            <PreviewActionBar addToRecord={addToRecord} downloadUrl={downloadUrl} />
            <DocumentRender
                documentUrl={previewUrl}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                responsiveControls={true}
            />
        </div>
    );
};

interface PreviewActionBarProps {
    addToRecord: AttachmentAddToRecordState;
    downloadUrl: string;
}

const PreviewActionBar: React.FC<PreviewActionBarProps> = props => {
    const { addToRecord, downloadUrl } = props;
    return (
        <div className="flex justify-end items-center gap-2">
            <a href={downloadUrl}>
                <SecondaryButton>Download</SecondaryButton>
            </a>
            <AddToRecordPreviewButton state={addToRecord} />
        </div>
    );
};

const AddToRecordPreviewButton: React.FC<{
    state: AttachmentAddToRecordState;
}> = props => {
    const { state } = props;
    if (state.kind === "added") {
        return (
            <span
                className="inline-flex items-center gap-1 py-2 px-4 text-base font-medium text-green-700 bg-green-50 border border-green-200 rounded-md"
                title="Saved to this record">
                <CheckCircleIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                Added
            </span>
        );
    }
    if (state.kind === "pending") {
        return <PrimaryButton disabled>Adding…</PrimaryButton>;
    }
    return (
        <PrimaryButton onClick={state.onAdd} title={state.tooltip}>
            {state.label}
        </PrimaryButton>
    );
};
